import { staleTimes, useInfiniteQuery, type ResourceKind } from '@platform/api-client';
import { useState } from 'react';

export const LIST_PAGE_SIZE = 50;

export const sizedPageParams = (pageToken: string | null, pageSize: number) => ({
  page_size: pageSize,
  ...(pageToken === null ? {} : { page_token: pageToken }),
});

export const pageParams = (pageToken: string | null) => sizedPageParams(pageToken, LIST_PAGE_SIZE);

export type KeysetPage<Item> = {
  readonly items: readonly Item[];
  readonly total: number;
  readonly nextPageToken: string | null;
};

export const REFERENCE_PAGE_SIZE = 100;
const MAX_REFERENCE_PAGES = 20;

export const referencePageParams = (pageToken: string | null) => ({
  page_size: REFERENCE_PAGE_SIZE,
  ...(pageToken === null ? {} : { page_token: pageToken }),
});

export const fetchAllPages = async <Item>(
  fetchPage: (pageToken: string | null) => Promise<KeysetPage<Item>>,
): Promise<KeysetPage<Item>> => {
  const items: Item[] = [];
  let pageToken: string | null = null;
  let total = 0;
  for (let page = 0; page < MAX_REFERENCE_PAGES; page += 1) {
    const answer: KeysetPage<Item> = await fetchPage(pageToken);
    items.push(...answer.items);
    total = answer.total;
    pageToken = answer.nextPageToken;
    if (pageToken === null) break;
  }
  return { items, total, nextPageToken: pageToken };
};

export type KeysetSource<Item> = {
  readonly queryKey: readonly unknown[];
  readonly fetchPage: (pageToken: string | null) => Promise<KeysetPage<Item>>;
  readonly resource: ResourceKind;
  readonly enabled?: boolean;
};

export const useKeysetPages = <Item>({
  queryKey,
  fetchPage,
  resource,
  enabled = true,
}: KeysetSource<Item>) => {
  const query = useInfiniteQuery({
    queryKey,
    enabled,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => fetchPage(pageParam),
    getNextPageParam: (last) => last.nextPageToken ?? undefined,
    staleTime: staleTimes[resource],
  });
  const pages = query.data?.pages ?? [];
  return {
    query,
    firstPage: pages[0],
    items: pages.flatMap((page): readonly Item[] => page.items),
    total: pages[0]?.total ?? 0,
    hasMore: query.hasNextPage,
    loadingMore: query.isFetchingNextPage,
    loadMore: () => {
      void query.fetchNextPage();
    },
  };
};

const FIRST_PAGE = 1;

export const useKeysetPager = <Item>(source: KeysetSource<Item>) => {
  const { query, total } = useKeysetPages(source);
  const sourceKey = JSON.stringify(source.queryKey);
  const [position, setPosition] = useState({ sourceKey, page: FIRST_PAGE });
  const page = position.sourceKey === sourceKey ? position.page : FIRST_PAGE;
  const loaded = query.data?.pages ?? [];
  const shown = loaded[page - 1];
  return {
    query: { ...query, isPending: query.isPending || shown === undefined },
    items: shown?.items ?? [],
    total,
    page,
    turnTo: (next: number) => {
      if (next > loaded.length) void query.fetchNextPage();
      setPosition({ sourceKey, page: next });
    },
  };
};
